import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp, query, where, getDocs, getCountFromServer } from "firebase/firestore";
import SEO from "../components/SEO";
import TrustBadges from "../components/TrustBadges";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

function DonateNew() {
  const { i18n } = useTranslation();
  const [donationType, setDonationType] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedLabel, setCopiedLabel] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Statistics from Firebase
  const [totalDonors, setTotalDonors] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalProjects, setTotalProjects] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Donation form state
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [donorPhone, setDonorPhone] = useState('');
  const [donationAmount, setDonationAmount] = useState('');
  const [itemDescription, setItemDescription] = useState(''); // สำหรับพัสดุ
  const [slipFile, setSlipFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Fetch real statistics from Firebase
  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setStatsLoading(true);
        
        // Get total approved donors count
        const donorsQuery = query(collection(db, 'donations'), where('status', '==', 'approved'));
        const donorsSnapshot = await getCountFromServer(donorsQuery);
        const donorsCount = donorsSnapshot.data().count;
        setTotalDonors(donorsCount);
        
        // Get total donation amount (only money donations)
        const donationsSnapshot = await getDocs(donorsQuery);
        let sum = 0;
        donationsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.amount && typeof data.amount === 'number') {
            sum += data.amount;
          }
        });
        setTotalAmount(sum);
        
        // Get total activities/projects count
        const activitiesSnapshot = await getCountFromServer(collection(db, 'activities'));
        setTotalProjects(activitiesSnapshot.data().count);
        
        setStatsLoading(false);
      } catch (error) {
        console.error('Error fetching statistics:', error);
        setStatsLoading(false);
      }
    };
    
    fetchStatistics();
  }, []);

  const bankInfo = {
    bankName: i18n.language === 'en' ? "Siam Commercial Bank" : "ธนาคารไทยพาณิชย์",
    accountNumber: "202-269023-1",
    accountName: i18n.language === 'en' ? "Sharing Center Mirror Foundation" : "ศูนย์แบ่งต่อ มูลนิธิกระจกเงา",
  };

  const donationLocation = {
    name: i18n.language === 'en' ? "Sharing Center by Mirror Foundation" : "ศูนย์แบ่งต่อ มูลนิธิกระจกเงา",
    address: "เลขที่ 191 ซ.รัตนดี 62 (แยก4-7) ถ.วิภาวดีรังสิต แขวงสลาอุบล เขตหลักสี่ กรุงเทพฯ 10210",
    latitude: 13.8782,
    longitude: 100.5623,
    phone: "061-909-1840",
    hours: i18n.language === 'en' ? "Mon-Sun 9:00-18:00" : "จันทร์-อาทิตย์ 9:00-18:00 น.",
  };

  const handleCopyAccountNumber = () => {
    navigator.clipboard.writeText(bankInfo.accountNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyAddress = () => {
    const fullAddress = `${donationLocation.name}\n${donationLocation.address}\n${i18n.language === 'en' ? 'Tel' : 'โทร'}: ${donationLocation.phone}`;
    navigator.clipboard.writeText(fullAddress);
    setCopiedAddress(true);
    setTimeout(() => setCopiedAddress(false), 2000);
  };

  const handleCopyParcelLabel = () => {
    const parcelLabel = `${i18n.language === 'en' ? 'To' : 'ถึง'}: ${donationLocation.name}
${donationLocation.address}
${i18n.language === 'en' ? 'Tel' : 'โทร'}: ${donationLocation.phone}

*** ${i18n.language === 'en' ? 'Donation' : 'บริจาคสิ่งของ'} ***`;
    navigator.clipboard.writeText(parcelLabel);
    setCopiedLabel(true);
    setTimeout(() => setCopiedLabel(false), 2000);
  };

  const handleSlipFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setSubmitError(i18n.language === 'en' ? 'File size must not exceed 5MB' : 'ขนาดไฟล์ต้องไม่เกิน 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setSubmitError(i18n.language === 'en' ? 'Please select an image file only' : 'กรุณาเลือกไฟล์รูปภาพเท่านั้น');
        return;
      }
      setSlipFile(file);
      setSubmitError('');
    }
  };

  const handleUploadSlip = async () => {
    if (!slipFile) return null;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', slipFile);
      formData.append('folder', donationType === 'money' ? 'donation-slips' : 'shipping-proofs');

      // อัปโหลดผ่าน Cloud Function เพื่อหลีกเลี่ยงปัญหา CORS
      const functionUrl = 'https://uploaddonationslip-o3lb6qf5cq-uc.a.run.app';
      
      const xhr = new XMLHttpRequest();
      
      return new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText);
            setUploading(false);
            setTimeout(() => setUploadProgress(0), 500);
            resolve(response.url);
          } else {
            setUploading(false);
            setUploadProgress(0);
            const error = new Error('Upload failed');
            setSubmitError('อัปโหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
            reject(error);
          }
        });

        xhr.addEventListener('error', () => {
          setUploading(false);
          setUploadProgress(0);
          const error = new Error('Network error');
          setSubmitError('เกิดข้อผิดพลาดในการอัปโหลด กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต');
          reject(error);
        });

        xhr.open('POST', functionUrl);
        xhr.send(formData);
      });
    } catch (error) {
      console.error("Upload error:", error);
      setUploading(false);
      setUploadProgress(0);
      setSubmitError('เกิดข้อผิดพลาดในการอัปโหลด');
      return null;
    }
  };

  const handleSubmitDonation = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);

    // Validation
    if (!donorName || !donorEmail || !donorPhone) {
      setSubmitError(i18n.language === 'en' ? 'Please fill in all fields' : 'กรุณากรอกข้อมูลให้ครบทุกช่อง');
      return;
    }

    // ถ้าเป็นบริจาคเงิน ต้องมีจำนวนเงินและสลิป
    if (donationType === 'money' && !donationAmount) {
      setSubmitError(i18n.language === 'en' ? 'Please enter donation amount' : 'กรุณาระบุจำนวนเงิน');
      return;
    }

    // ถ้าเป็นบริจาคพัสดุ ต้องมีรายละเอียดสิ่งของ
    if (donationType === 'items' && !itemDescription) {
      setSubmitError(i18n.language === 'en' ? 'Please describe the items' : 'กรุณาระบุรายละเอียดสิ่งของ');
      return;
    }

    if (!slipFile) {
      const fileLabel = donationType === 'money' 
        ? (i18n.language === 'en' ? 'payment slip' : 'สลิปการโอนเงิน')
        : (i18n.language === 'en' ? 'shipping proof' : 'หลักฐานการส่ง');
      setSubmitError(i18n.language === 'en' ? `Please upload ${fileLabel}` : `กรุณาอัปโหลด${fileLabel}`);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Upload slip
      const slipURL = await handleUploadSlip();
      
      if (!slipURL) {
        throw new Error('Upload returned null URL');
      }

      // Save to Firestore
      const donationData = {
        donorName,
        email: donorEmail,
        phone: donorPhone,
        slipURL,
        status: 'pending',
        createdAt: serverTimestamp(),
        approvedAt: null,
        approvedBy: null,
        note: '',
        language: i18n.language,
        donationType: donationType // 'money' หรือ 'items'
      };

      // เพิ่มข้อมูลตามประเภท
      if (donationType === 'money') {
        donationData.amount = parseFloat(donationAmount);
        donationData.paymentMethod = 'bank-transfer';
      } else {
        donationData.itemDescription = itemDescription;
        donationData.donationMethod = 'items';
      }

      await addDoc(collection(db, 'donations'), donationData);

      setSubmitSuccess(true);
      setSubmitError('');
      setShowConfetti(true);

      // Reset form
      setDonorName('');
      setDonorEmail('');
      setDonorPhone('');
      setDonationAmount('');
      setItemDescription('');
      setSlipFile(null);

      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Reset after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setShowConfetti(false);
        setDonationType(null);
      }, 5000);

    } catch (error) {
      console.error('Error submitting donation:', error);
      setSubmitError(i18n.language === 'en' ? 'Submission failed, please try again' : 'ส่งข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <SEO
        title={i18n.language === 'en' ? "Donate - Make a Difference" : "บริจาค - ร่วมสร้างการเปลี่ยนแปลง"}
        description={i18n.language === 'en'
          ? "Support our mission by donating money or items. Every contribution creates positive change and helps those in need."
          : "ร่วมสนับสนุนพันธกิจของเราด้วยการบริจาคเงินหรือสิ่งของ ทุกการสนับสนุนสร้างการเปลี่ยนแปลงที่ดีและช่วยเหลือผู้ที่ต้องการความช่วยเหลือ"}
        keywords="บริจาคเงิน, บริจาคสิ่งของ, donate money, donate items, charity donation, ศูนย์รับบริจาค, มูลนิธิกระจกเงา"
        url="https://mirror-foundation-bytony.com/donate"
        type="website"
      />
      <div style={{ paddingTop: '80px', minHeight: '100vh', background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 50%, #80deea 100%)', position: 'relative' }}>
        {/* Confetti Animation on Success */}
      <AnimatePresence>
        {showConfetti && (
          <>
            {[...Array(50)].map((_, i) => (
              <motion.div
                key={i}
                initial={{
                  position: 'fixed',
                  top: '0%',
                  left: `${Math.random() * 100}%`,
                  fontSize: '2rem',
                  zIndex: 9999,
                }}
                animate={{
                  top: '100vh',
                  rotate: [0, 360, 720],
                  opacity: [1, 1, 0],
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  ease: 'linear',
                }}
              >
                {['❤️', '💝', '🎉', '⭐', '💖', '🌟', '✨'][Math.floor(Math.random() * 7)]}
              </motion.div>
            ))}
          </>
        )}
      </AnimatePresence>

      {/* Hero Section with Charity Theme */}
      <section style={{
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        padding: '5rem 0 4rem',
        marginBottom: '0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated Background Elements */}
        <motion.div
          style={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            fontSize: '8rem',
            opacity: '0.1'
          }}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, 0]
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          🤝
        </motion.div>
        <motion.div
          style={{
            position: 'absolute',
            bottom: '10%',
            left: '8%',
            fontSize: '6rem',
            opacity: '0.1'
          }}
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 0]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        >
          💙
        </motion.div>

        <div className="container-modern text-center" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            style={{
              fontSize: '5rem',
              marginBottom: '1rem'
            }}
            initial={{ scale: 0 }}
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3
            }}
          >
            🎁
          </motion.div>

          <motion.h1
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              fontWeight: '900',
              color: 'white',
              marginBottom: '1rem',
              textShadow: '0 4px 20px rgba(0,0,0,0.2)',
              letterSpacing: '-0.02em'
            }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {i18n.language === 'en' ? 'Make Someone\'s Life Better' : 'ร่วมสร้างรอยยิ้มให้ผู้อื่น'}
          </motion.h1>

          <motion.p
            style={{
              fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
              color: 'rgba(255, 255, 255, 0.95)',
              maxWidth: '800px',
              margin: '0 auto 2rem',
              lineHeight: '1.8',
              fontWeight: '400'
            }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {i18n.language === 'en'
              ? 'Every donation creates ripples of positive change. Join us in making a difference today.'
              : 'ทุกการบริจาคสร้างการเปลี่ยนแปลงที่ดี ร่วมสร้างความเปลี่ยนแปลงไปกับเรา'}
          </motion.p>

          <motion.div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginTop: '2.5rem'
            }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {/* Statistics Cards */}
            {[
              { 
                icon: '👥', 
                label: i18n.language === 'en' ? 'Total Donors' : 'ผู้บริจาคทั้งหมด', 
                value: statsLoading ? '...' : `${totalDonors.toLocaleString()}+`,
                rawValue: totalDonors
              },
              { 
                icon: '💰', 
                label: i18n.language === 'en' ? 'Funds Raised' : 'เงินที่ระดมได้', 
                value: statsLoading ? '...' : totalAmount >= 1000000 ? `฿${(totalAmount / 1000000).toFixed(1)}M+` : `฿${totalAmount.toLocaleString()}+`,
                rawValue: totalAmount
              },
              { 
                icon: '🎯', 
                label: i18n.language === 'en' ? 'Projects' : 'โครงการ', 
                value: statsLoading ? '...' : `${totalProjects}+`,
                rawValue: totalProjects
              }
            ].map((stat, index) => (
              <motion.div
                key={index}
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  padding: '1.5rem 1rem',
                  minWidth: '150px',
                  flex: '1 1 150px',
                  border: '2px solid rgba(255, 255, 255, 0.2)'
                }}
                whileHover={{ 
                  scale: 1.05,
                  background: 'rgba(255, 255, 255, 0.25)',
                  y: -5
                }}
                animate={{
                  y: [0, -10, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: index * 0.3
                }}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>{stat.icon}</div>
                <div style={{ 
                  fontSize: '1.75rem', 
                  fontWeight: '800', 
                  color: 'white',
                  marginBottom: '0.25rem'
                }}>
                  {stat.value}
                </div>
                <div style={{ 
                  fontSize: '0.9rem', 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: '500'
                }}>
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <div className="container-modern" style={{ paddingBottom: '4rem', marginTop: '-3rem', position: 'relative', zIndex: 2 }}>
        {/* Trust Badges */}
        <TrustBadges />
        {/* Selection Cards */}
        {!donationType && (
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <motion.div
              className="text-center mb-5"
              style={{
                background: 'white',
                borderRadius: '30px',
                padding: '2.5rem',
                boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                marginBottom: '3rem'
              }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                style={{ fontSize: '4rem', marginBottom: '1rem' }}
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity
                }}
              >
                🎯
              </motion.div>
              <h2 style={{ 
                fontSize: '2rem', 
                fontWeight: '800', 
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '0.75rem'
              }}>
                {i18n.language === 'en' ? 'Choose How You\'d Like to Help' : 'เลือกวิธีการช่วยเหลือ'}
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#64748b', maxWidth: '600px', margin: '0 auto' }}>
                {i18n.language === 'en' 
                  ? 'Every contribution makes a real difference in someone\'s life'
                  : 'ทุกการสนับสนุนสร้างความเปลี่ยนแปลงที่แท้จริงในชีวิตของผู้อื่น'}
              </p>
            </motion.div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
              gap: '2rem'
            }}>
              <motion.div
                onClick={() => setDonationType('money')}
                style={{
                  background: 'linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)',
                  borderRadius: '30px',
                  padding: '3rem 2rem',
                  cursor: 'pointer',
                  boxShadow: '0 10px 30px rgba(79, 195, 247, 0.3)',
                  border: 'none',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: '0 20px 40px rgba(79, 195, 247, 0.4)',
                  y: -10
                }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Animated background circles */}
                <motion.div
                  style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                  }}
                  animate={{
                    scale: [1, 1.2, 1],
                    rotate: [0, 180, 360]
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity
                  }}
                />
                
                <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                  <motion.div
                    style={{ fontSize: '5rem', marginBottom: '1rem' }}
                    animate={{
                      y: [0, -20, 0]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity
                    }}
                  >
                    💰
                  </motion.div>
                  <h3 style={{ 
                    fontSize: '2rem', 
                    fontWeight: '800', 
                    color: 'white',
                    marginBottom: '1rem',
                    textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                  }}>
                    {i18n.language === 'en' ? 'Donate Money' : 'บริจาคเงิน'}
                  </h3>
                  <p style={{ 
                    fontSize: '1.05rem', 
                    color: 'rgba(255,255,255,0.95)',
                    lineHeight: '1.7',
                    marginBottom: '1.5rem'
                  }}>
                    {i18n.language === 'en' 
                      ? 'Support our programs with financial contribution'
                      : 'สนับสนุนโครงการของเราด้วยเงินสด'}
                  </p>
                  <motion.div
                    style={{
                      background: 'white',
                      color: '#29b6f6',
                      padding: '0.75rem 2rem',
                      borderRadius: '15px',
                      display: 'inline-block',
                      fontWeight: '700',
                      fontSize: '1.05rem'
                    }}
                    whileHover={{ scale: 1.1 }}
                  >
                    {i18n.language === 'en' ? 'Choose This' : 'เลือกตัวเลือกนี้'} →
                  </motion.div>
                </div>
              </motion.div>

              <motion.div
                onClick={() => setDonationType('items')}
                style={{
                  background: 'linear-gradient(135deg, #ba68c8 0%, #9c27b0 100%)',
                  borderRadius: '30px',
                  padding: '3rem 2rem',
                  cursor: 'pointer',
                  boxShadow: '0 10px 30px rgba(186, 104, 200, 0.3)',
                  border: 'none',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: '0 20px 40px rgba(186, 104, 200, 0.4)',
                  y: -10
                }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Animated background circles */}
                <motion.div
                  style={{
                    position: 'absolute',
                    bottom: '-50px',
                    left: '-50px',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                  }}
                  animate={{
                    scale: [1, 1.2, 1],
                    rotate: [360, 180, 0]
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity
                  }}
                />
                
                <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                  <motion.div
                    style={{ fontSize: '5rem', marginBottom: '1rem' }}
                    animate={{
                      y: [0, -20, 0]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: 0.5
                    }}
                  >
                    📦
                  </motion.div>
                  <h3 style={{ 
                    fontSize: '2rem', 
                    fontWeight: '800', 
                    color: 'white',
                    marginBottom: '1rem',
                    textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                  }}>
                    {i18n.language === 'en' ? 'Donate Items' : 'บริจาคสิ่งของ'}
                  </h3>
                  <p style={{ 
                    fontSize: '1.05rem', 
                    color: 'rgba(255,255,255,0.95)',
                    lineHeight: '1.7',
                    marginBottom: '1.5rem'
                  }}>
                    {i18n.language === 'en' 
                      ? 'Contribute goods and supplies to help others'
                      : 'บริจาคสิ่งของและอุปกรณ์เพื่อช่วยเหลือผู้อื่น'}
                  </p>
                  <motion.div
                    style={{
                      background: 'white',
                      color: '#9c27b0',
                      padding: '0.75rem 2rem',
                      borderRadius: '15px',
                      display: 'inline-block',
                      fontWeight: '700',
                      fontSize: '1.05rem'
                    }}
                    whileHover={{ scale: 1.1 }}
                  >
                    {i18n.language === 'en' ? 'Choose This' : 'เลือกตัวเลือกนี้'} →
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        )}

        {/* Money Donation Details */}
        {donationType === 'money' && (
          <motion.div
            style={{ maxWidth: '1000px', margin: '0 auto' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.button
              onClick={() => {
                setDonationType(null);
                setSubmitSuccess(false);
                setSubmitError('');
              }}
              style={{
                background: 'white',
                color: '#1e3c72',
                border: '2px solid #1e3c72',
                padding: '0.75rem 1.5rem',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginBottom: '1.5rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 2px 8px rgba(30, 60, 114, 0.1)'
              }}
              whileHover={{ 
                scale: 1.05,
                boxShadow: '0 4px 12px rgba(30, 60, 114, 0.2)'
              }}
              whileTap={{ scale: 0.95 }}
            >
              ← {i18n.language === 'en' ? 'Back' : 'ย้อนกลับ'}
            </motion.button>

            {/* Success Message */}
            {submitSuccess && (
              <motion.div
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '2rem',
                  borderRadius: 'var(--radius-2xl)',
                  marginBottom: '2rem',
                  textAlign: 'center',
                  boxShadow: 'var(--shadow-xl)'
                }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✅</h2>
                <h3 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                  {i18n.language === 'en' ? 'Thank You for Your Donation!' : 'ขอบคุณสำหรับการบริจาค!'}
                </h3>
                <p style={{ fontSize: '1.1rem', opacity: 0.95 }}>
                  {i18n.language === 'en' 
                    ? 'Your donation has been received and is under review. We will notify you via email once approved.'
                    : 'เราได้รับการบริจาคของคุณแล้ว และกำลังตรวจสอบ เราจะแจ้งผลการอนุมัติผ่านอีเมล'}
                </p>
              </motion.div>
            )}

            {/* Error Message */}
            {submitError && (
              <motion.div
                style={{
                  background: '#fee2e2',
                  color: '#991b1b',
                  padding: '1rem 1.5rem',
                  borderRadius: 'var(--radius-lg)',
                  marginBottom: '2rem',
                  border: '2px solid #fca5a5'
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <strong>⚠️ {submitError}</strong>
              </motion.div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
              gap: '2rem'
            }}>
              {/* Bank Info */}
              <div>
                <div style={{
                  background: 'white',
                  borderRadius: 'var(--radius-2xl)',
                  padding: '2rem',
                  boxShadow: 'var(--shadow-lg)'
                }}>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--gray-900)', marginBottom: '1.5rem' }}>
                    {i18n.language === 'en' ? 'Bank Account Information' : 'ข้อมูลบัญชีธนาคาร'}
                  </h3>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      {i18n.language === 'en' ? 'Bank Name' : 'ชื่อธนาคาร'}
                    </label>
                    <div style={{
                      background: 'var(--gray-50)',
                      padding: '0.875rem 1.25rem',
                      borderRadius: 'var(--radius-lg)',
                      fontSize: '1rem',
                      fontWeight: '600'
                    }}>
                      {bankInfo.bankName}
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      {i18n.language === 'en' ? 'Account Number' : 'เลขที่บัญชี'}
                    </label>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <div style={{
                        flex: 1,
                        background: 'var(--gray-50)',
                        padding: '0.875rem 1.25rem',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '1.2rem',
                        fontWeight: '700',
                        letterSpacing: '1px',
                        color: 'var(--primary-blue)'
                      }}>
                        {bankInfo.accountNumber}
                      </div>
                      <button
                        onClick={handleCopyAccountNumber}
                        className="modern-btn-primary"
                        style={{ padding: '0.75rem 1.25rem', fontSize: '0.9rem' }}
                      >
                        {copied ? '✓' : '📋'}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      {i18n.language === 'en' ? 'Account Name' : 'ชื่อบัญชี'}
                    </label>
                    <div style={{
                      background: 'var(--gray-50)',
                      padding: '0.875rem 1.25rem',
                      borderRadius: 'var(--radius-lg)',
                      fontSize: '1rem',
                      fontWeight: '600'
                    }}>
                      {bankInfo.accountName}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Donation Form */}
              <div style={{
                background: 'white',
                borderRadius: 'var(--radius-2xl)',
                padding: '2.5rem',
                boxShadow: 'var(--shadow-xl)'
              }}>
                <h3 style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--gray-900)', marginBottom: '1.5rem' }}>
                  📝 {i18n.language === 'en' ? 'Donation Information' : 'ข้อมูลการบริจาค'}
                </h3>

                <form onSubmit={handleSubmitDonation}>
                  {/* Donor Name */}
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                      {i18n.language === 'en' ? 'Full Name' : 'ชื่อ-นามสกุล'} *
                    </label>
                    <input
                      type="text"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      placeholder={i18n.language === 'en' ? 'Enter your name' : 'กรอกชื่อของคุณ'}
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem 1.25rem',
                        border: '2px solid var(--gray-200)',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '1rem',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--primary-blue)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--gray-200)'}
                    />
                  </div>

                  {/* Email */}
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                      {i18n.language === 'en' ? 'Email' : 'อีเมล'} *
                    </label>
                    <input
                      type="email"
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      placeholder={i18n.language === 'en' ? 'your@email.com' : 'your@email.com'}
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem 1.25rem',
                        border: '2px solid var(--gray-200)',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '1rem'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--primary-blue)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--gray-200)'}
                    />
                  </div>

                  {/* Phone */}
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                      {i18n.language === 'en' ? 'Phone Number' : 'เบอร์โทรศัพท์'} *
                    </label>
                    <input
                      type="tel"
                      value={donorPhone}
                      onChange={(e) => setDonorPhone(e.target.value)}
                      placeholder={i18n.language === 'en' ? '0812345678' : '0812345678'}
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem 1.25rem',
                        border: '2px solid var(--gray-200)',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '1rem'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--primary-blue)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--gray-200)'}
                    />
                  </div>

                  {/* Amount */}
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                      {i18n.language === 'en' ? 'Donation Amount (THB)' : 'จำนวนเงิน (บาท)'} *
                    </label>
                    <input
                      type="number"
                      value={donationAmount}
                      onChange={(e) => setDonationAmount(e.target.value)}
                      placeholder={i18n.language === 'en' ? 'Enter amount' : 'กรอกจำนวนเงิน'}
                      min="1"
                      step="0.01"
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem 1.25rem',
                        border: '2px solid var(--gray-200)',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        color: 'var(--primary-blue)'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--primary-blue)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--gray-200)'}
                    />
                  </div>

                  {/* Upload Slip */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                      {i18n.language === 'en' ? 'Upload Payment Slip' : 'อัปโหลดสลิปการโอนเงิน'} *
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleSlipFileChange}
                      disabled={uploading}
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px dashed var(--gray-300)',
                        borderRadius: 'var(--radius-lg)',
                        cursor: 'pointer'
                      }}
                    />
                    {slipFile && !uploading && (
                      <div style={{ 
                        marginTop: '0.5rem', 
                        fontSize: '0.9rem', 
                        color: 'var(--primary-blue)',
                        fontWeight: '500'
                      }}>
                        ✓ {slipFile.name}
                      </div>
                    )}
                    {uploading && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <div style={{
                          background: 'var(--gray-200)',
                          height: '8px',
                          borderRadius: '999px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            background: 'var(--primary-blue)',
                            height: '100%',
                            width: `${uploadProgress}%`,
                            transition: 'width 0.3s ease'
                          }}></div>
                        </div>
                        <div style={{ 
                          textAlign: 'center', 
                          marginTop: '0.5rem',
                          fontSize: '0.85rem',
                          color: 'var(--gray-600)'
                        }}>
                          {i18n.language === 'en' ? 'Uploading' : 'กำลังอัปโหลด'}... {Math.round(uploadProgress)}%
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit Button with Pulse Animation */}
                  <motion.button
                    type="submit"
                    disabled={uploading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      background: uploading ? 'var(--gray-400)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 'var(--radius-lg)',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      boxShadow: 'var(--shadow-md)',
                    }}
                    whileHover={!uploading ? {
                      scale: 1.02,
                      boxShadow: '0 10px 30px rgba(16, 185, 129, 0.4)',
                      y: -3
                    } : {}}
                    whileTap={!uploading ? { scale: 0.98 } : {}}
                    animate={!uploading ? {
                      boxShadow: [
                        '0 4px 6px rgba(0,0,0,0.1)',
                        '0 8px 20px rgba(16, 185, 129, 0.3)',
                        '0 4px 6px rgba(0,0,0,0.1)'
                      ]
                    } : {}}
                    transition={{
                      boxShadow: {
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }
                    }}
                  >
                    {uploading
                      ? (i18n.language === 'en' ? 'Submitting...' : 'กำลังส่งข้อมูล...')
                      : (i18n.language === 'en' ? '💝 Submit Donation' : '💝 ส่งข้อมูลการบริจาค')
                    }
                  </motion.button>

                  <p style={{
                    marginTop: '1rem',
                    fontSize: '0.85rem',
                    color: 'var(--gray-600)',
                    textAlign: 'center',
                    lineHeight: '1.5'
                  }}>
                    {i18n.language === 'en' 
                      ? 'After submitting, an admin will review your donation and notify you via email.'
                      : 'หลังจากส่งข้อมูล ทีมงานจะตรวจสอบและแจ้งผลการอนุมัติผ่านอีเมล'}
                  </p>
                </form>
              </div>
            </div>
          </motion.div>
        )}

        {/* Items Donation Details */}
        {donationType === 'items' && (
          <motion.div
            style={{ maxWidth: '1200px', margin: '0 auto' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.button
              onClick={() => setDonationType(null)}
              style={{
                background: 'white',
                color: '#1e3c72',
                border: '2px solid #1e3c72',
                padding: '0.75rem 1.5rem',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginBottom: '1.5rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 2px 8px rgba(30, 60, 114, 0.1)'
              }}
              whileHover={{ 
                scale: 1.05,
                boxShadow: '0 4px 12px rgba(30, 60, 114, 0.2)'
              }}
              whileTap={{ scale: 0.95 }}
            >
              ← {i18n.language === 'en' ? 'Back' : 'ย้อนกลับ'}
            </motion.button>

            {/* Success Message */}
            {submitSuccess && (
              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                padding: '1.5rem',
                borderRadius: 'var(--radius-xl)',
                marginBottom: '2rem',
                textAlign: 'center',
                boxShadow: 'var(--shadow-lg)'
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                  {i18n.language === 'en' ? 'Thank you for your donation!' : 'ขอบคุณสำหรับการบริจาค!'}
                </h3>
                <p style={{ opacity: 0.9 }}>
                  {i18n.language === 'en' 
                    ? 'We will review your donation and notify you soon.'
                    : 'ทีมงานจะตรวจสอบและแจ้งผลให้ทราบเร็วๆ นี้'}
                </p>
              </div>
            )}

            {/* Error Message */}
            {submitError && (
              <div style={{
                background: '#fee2e2',
                color: '#dc2626',
                padding: '1rem',
                borderRadius: 'var(--radius-lg)',
                marginBottom: '2rem',
                borderLeft: '4px solid #dc2626'
              }}>
                {submitError}
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
              gap: '2rem'
            }}>
              {/* ข้อมูลศูนย์รับบริจาค */}
              <div>
                <div style={{
                  background: 'white',
                  borderRadius: 'var(--radius-2xl)',
                  padding: '2rem',
                  boxShadow: 'var(--shadow-lg)',
                  marginBottom: '2rem'
                }}>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--gray-900)', marginBottom: '1.5rem' }}>
                    {i18n.language === 'en' ? 'Donation Center Information' : 'ข้อมูลศูนย์รับบริจาค'}
                  </h3>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      {i18n.language === 'en' ? 'Location Name' : 'ชื่อสถานที่'}
                    </label>
                    <div style={{
                      background: 'var(--gray-50)',
                      padding: '0.875rem 1.25rem',
                      borderRadius: 'var(--radius-lg)',
                      fontSize: '1rem',
                      fontWeight: '600'
                    }}>
                      {donationLocation.name}
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      {i18n.language === 'en' ? 'Address' : 'ที่อยู่'}
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <div style={{
                        flex: 1,
                        background: 'var(--gray-50)',
                        padding: '0.875rem 1.25rem',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '0.9rem',
                        lineHeight: '1.5'
                      }}>
                        {donationLocation.address}
                      </div>
                      <button
                        type="button"
                        onClick={handleCopyAddress}
                        style={{
                          padding: '0.875rem',
                          background: 'var(--primary-blue)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 'var(--radius-lg)',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {copiedAddress ? '✓' : '📋'} {i18n.language === 'en' ? 'Copy' : 'คัดลอก'}
                      </button>
                    </div>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                        📞 {i18n.language === 'en' ? 'Phone' : 'โทรศัพท์'}
                      </label>
                      <div style={{
                        background: 'var(--gray-50)',
                        padding: '0.875rem 1.25rem',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '0.95rem',
                        fontWeight: '600'
                      }}>
                        {donationLocation.phone}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                        🕐 {i18n.language === 'en' ? 'Hours' : 'เวลาทำการ'}
                      </label>
                      <div style={{
                        background: 'var(--gray-50)',
                        padding: '0.875rem 1.25rem',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}>
                        {donationLocation.hours}
                      </div>
                    </div>
                  </div>

                  <div className="text-center" style={{ marginTop: '1.5rem' }}>
                    <button
                      type="button"
                      onClick={handleCopyParcelLabel}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-lg)',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: '600'
                      }}
                    >
                      {copiedLabel ? '✓ ' + (i18n.language === 'en' ? 'Copied!' : 'คัดลอกแล้ว!') : '📄 ' + (i18n.language === 'en' ? 'Copy Parcel Label' : 'คัดลอกหน้าพัสดุ')}
                    </button>
                  </div>
                </div>

                {/* แผนที่ */}
                <div style={{
                  borderRadius: 'var(--radius-2xl)',
                  overflow: 'hidden',
                  boxShadow: 'var(--shadow-xl)',
                  height: '300px'
                }}>
                  <MapContainer
                    center={[donationLocation.latitude, donationLocation.longitude]}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Marker position={[donationLocation.latitude, donationLocation.longitude]}>
                      <Popup>
                        <strong>{donationLocation.name}</strong>
                        <br />
                        {donationLocation.address}
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>

              {/* ฟอร์มบริจาคพัสดุ */}
              <div>
                <form onSubmit={handleSubmitDonation} style={{
                  background: 'white',
                  borderRadius: 'var(--radius-2xl)',
                  padding: '2rem',
                  boxShadow: 'var(--shadow-lg)'
                }}>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--gray-900)', marginBottom: '0.5rem', textAlign: 'center' }}>
                    📝 {i18n.language === 'en' ? 'Item Donation Form' : 'ฟอร์มบริจาคสิ่งของ'}
                  </h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '2rem', textAlign: 'center' }}>
                    {i18n.language === 'en' 
                      ? 'After shipping, please upload proof of delivery'
                      : 'หลังจากส่งของแล้ว กรุณาอัปโหลดหลักฐานการส่ง'}
                  </p>

                  {/* ชื่อผู้บริจาค */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                      {i18n.language === 'en' ? 'Your Name' : 'ชื่อ-นามสกุล'} *
                    </label>
                    <input
                      type="text"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      disabled={uploading}
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem 1.25rem',
                        border: '2px solid var(--gray-300)',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* อีเมล */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                      {i18n.language === 'en' ? 'Email' : 'อีเมล'} *
                    </label>
                    <input
                      type="email"
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      disabled={uploading}
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem 1.25rem',
                        border: '2px solid var(--gray-300)',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* เบอร์โทรศัพท์ */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                      {i18n.language === 'en' ? 'Phone Number' : 'เบอร์โทรศัพท์'} *
                    </label>
                    <input
                      type="tel"
                      value={donorPhone}
                      onChange={(e) => setDonorPhone(e.target.value)}
                      disabled={uploading}
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem 1.25rem',
                        border: '2px solid var(--gray-300)',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* รายละเอียดสิ่งของ */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                      {i18n.language === 'en' ? 'Item Description' : 'รายละเอียดสิ่งของ'} *
                    </label>
                    <textarea
                      value={itemDescription}
                      onChange={(e) => setItemDescription(e.target.value)}
                      disabled={uploading}
                      required
                      rows={4}
                      placeholder={i18n.language === 'en' 
                        ? 'e.g., Used clothes, books, toys, etc.'
                        : 'เช่น เสื้อผ้าเก่า, หนังสือ, ของเล่น, เครื่องใช้ไฟฟ้า เป็นต้น'}
                      style={{
                        width: '100%',
                        padding: '0.875rem 1.25rem',
                        border: '2px solid var(--gray-300)',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  {/* อัปโหลดหลักฐาน */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                      {i18n.language === 'en' ? 'Upload Shipping Proof' : 'อัปโหลดหลักฐานการส่ง'} *
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleSlipFileChange}
                      disabled={uploading}
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px dashed var(--gray-300)',
                        borderRadius: 'var(--radius-lg)',
                        cursor: 'pointer'
                      }}
                    />
                    {slipFile && !uploading && (
                      <div style={{ 
                        marginTop: '0.5rem', 
                        fontSize: '0.9rem', 
                        color: 'var(--primary-blue)',
                        fontWeight: '500'
                      }}>
                        ✓ {slipFile.name}
                      </div>
                    )}
                    {uploading && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <div style={{
                          background: 'var(--gray-200)',
                          height: '8px',
                          borderRadius: '999px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            background: '#10b981',
                            height: '100%',
                            width: `${uploadProgress}%`,
                            transition: 'width 0.3s ease'
                          }}></div>
                        </div>
                        <div style={{ 
                          textAlign: 'center', 
                          marginTop: '0.5rem',
                          fontSize: '0.85rem',
                          color: 'var(--gray-600)'
                        }}>
                          {i18n.language === 'en' ? 'Uploading' : 'กำลังอัปโหลด'}... {Math.round(uploadProgress)}%
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit Button with Pulse Animation */}
                  <motion.button
                    type="submit"
                    disabled={uploading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      background: uploading ? 'var(--gray-400)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 'var(--radius-lg)',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      boxShadow: 'var(--shadow-md)',
                    }}
                    whileHover={!uploading ? {
                      scale: 1.02,
                      boxShadow: '0 10px 30px rgba(16, 185, 129, 0.4)',
                      y: -3
                    } : {}}
                    whileTap={!uploading ? { scale: 0.98 } : {}}
                    animate={!uploading ? {
                      boxShadow: [
                        '0 4px 6px rgba(0,0,0,0.1)',
                        '0 8px 20px rgba(16, 185, 129, 0.3)',
                        '0 4px 6px rgba(0,0,0,0.1)'
                      ]
                    } : {}}
                    transition={{
                      boxShadow: {
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }
                    }}
                  >
                    {uploading
                      ? (i18n.language === 'en' ? 'Submitting...' : 'กำลังส่งข้อมูล...')
                      : (i18n.language === 'en' ? '💝 Submit Donation' : '💝 ส่งข้อมูลการบริจาค')
                    }
                  </motion.button>

                  <p style={{
                    marginTop: '1rem',
                    fontSize: '0.85rem',
                    color: 'var(--gray-600)',
                    textAlign: 'center',
                    lineHeight: '1.5'
                  }}>
                    {i18n.language === 'en' 
                      ? 'After submitting, an admin will review your donation and notify you via email.'
                      : 'หลังจากส่งข้อมูล ทีมงานจะตรวจสอบและแจ้งผลผ่านอีเมล'}
                  </p>
                </form>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
    </>
  );
}

export default DonateNew;
